import React, { useState } from "react";
import { AppProps } from "next/app";
import Head from "next/head";
import Script from "next/script";
import { Provider } from "react-redux";
import { createGlobalStyle } from "styled-components";
import {
  BCAThemeProvider,
  OverlayProvider,
  newBcaTheme,
} from "@bca/ui-library";
import { SSRProvider } from "@react-aria/ssr";

import { store } from "../redux/store";
import ErrorPage from "../components/ErrorPage/ErrorPage";
import AppInsightsService from "../services/AppInsightsService";
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { AuthProvider } from "@bca-auth0/bca-auth0-provider";
import { useRouter } from "next/router";

const GlobalStyle = createGlobalStyle`
  div#__next, html, body {
    margin: 0;
    min-height: 100%;
    min-width: 100%;
    font-family: "Roboto", "sans-serif";
    overflow-x: clip;
  }

  textarea {
    font-family: "Roboto", "sans-serif";
  }

  ul[role="menubar"] li:hover ul[role="submenu"],
  ul[role="menubar"] li:hover div[role="submenu"] {
    z-index: 2;
  }
`;

interface PageProps {
  user: { firstName: string; lastName: string };
  idToken: string;
  isToUseAuthZero: boolean;
  auth0Audience: string;
  auth0ClientId: string;
  auth0Domain: string;
  auth0Scope: string;
}

interface VehicleSearchAppPageProps extends AppProps {
  pageProps: PageProps;
  appInsightsConnectionString: string;
  authRedirectUri: string;
}

export default function BuyerApp({
  Component,
  pageProps,
  appInsightsConnectionString,
  authRedirectUri,
}: VehicleSearchAppPageProps) {
  const {
    user,
    idToken,
    isToUseAuthZero,
    auth0Audience,
    auth0ClientId,
    auth0Domain,
    auth0Scope,
  } = pageProps ?? {};
  const router = useRouter();

  const appInsightsInstance = AppInsightsService.getInstance(
    appInsightsConnectionString
  );
  const reactPlugin = appInsightsInstance.reactPlugin;

  const handleRedirectCallback = (props) => {
    router.replace(props.target || window.location.pathname);
  };

  const [isAdobeLaunchLoaded, setIsAdobeLaunchLoaded] = useState(false);

  const adobeLaunchSrc = {
    production:
      "https://assets.adobedtm.com/6d67b656c240/5ee6498fb93e/launch-1d21ac8c430f.min.js",
    preview:
      "https://assets.adobedtm.com/6d67b656c240/5ee6498fb93e/launch-8fa67916b053-staging.min.js",
  }[process.env.ENVIRONMENT] ?? null;

  return (
    <SSRProvider>
      {adobeLaunchSrc && (
        <Script
          src={adobeLaunchSrc}
          strategy="afterInteractive"
          onLoad={() => {
            setIsAdobeLaunchLoaded(true);
          }}
          onError={() =>
            console.error(
              `[Adobe Launch] Failed to load script (${process.env.ENVIRONMENT})`
            )
          }
        />
      )}
      <AuthProvider
        domain={auth0Domain}
        clientId={auth0ClientId}
        useRefreshTokens
        authorizationParams={{
          redirect_uri: authRedirectUri,
          audience: auth0Audience,
          scope: auth0Scope,
        }}
        onRedirectCallback={handleRedirectCallback}
      >
        <OverlayProvider>
          <GlobalStyle />
          <BCAThemeProvider theme={newBcaTheme}>
            <AppInsightsErrorBoundary
              onError={() => (
                <ErrorPage
                  user={user}
                  idToken={idToken}
                  isToUseAuthZero={isToUseAuthZero}
                />
              )}
              appInsights={reactPlugin}
            >
              <Provider store={store}>
                <Head>
                  <meta
                    name="viewport"
                    content="initial-scale=1.0, width=device-width"
                  />
                </Head>
                <Component {...pageProps} isAdobeLaunchLoaded={isAdobeLaunchLoaded} />
              </Provider>
            </AppInsightsErrorBoundary>
          </BCAThemeProvider>
        </OverlayProvider>
      </AuthProvider>
    </SSRProvider>
  );
}

BuyerApp.getInitialProps = async () => {
  return {
    appInsightsConnectionString:
      process.env.APPLICATIONINSIGHTS_CONNECTION_STRING,
    authRedirectUri: process.env.AUTH_PROVIDER_REDIRECT_URI,
  };
};
